/**
 * `useUiStore` — the session/UI slice (frontend.md §2.2).
 *
 * Small, global, client-only UI state: which surface is active, nav collapse, the command
 * palette open flag, the theme. Deliberately NOT the place for shareable surface state —
 * search query, selected entity, graph focal note all live in the URL (frontend.md §2.2,
 * §2.3), so the command palette, back/forward, and saved searches are trivial.
 *
 * `theme` is persisted to `localStorage` directly (a one-key persistence, not worth
 * `zustand/middleware/persist` overhead); everything else is ephemeral per session.
 */

import { create } from 'zustand';
import type { SurfaceId } from '@/routes';

/**
 * The available themes.
 *
 * 2026-05-30 redesign — three published themes:
 *   - `ember` — warm near-black (default for new installs)
 *   - `blue-slate` — cool neutral, elevation-via-shadow instead of borders
 *   - `parchment` — light, paper-warm
 * plus `slate` (restored 2026-05-31) and the three CRT terminal themes
 * (2026-06-09).
 *
 * Remaster U5 (2026-07-04): the seven hidden legacy themes (`light` / `oled`
 * / `forest` / `slate_light` / `lunar` / `ocean_floor` / `paper`) were DELETED
 * from tokens.css and this union. `loadTheme()` migrates any persisted legacy
 * value to its nearest surviving palette (see LEGACY_THEME_MIGRATION).
 *
 * `system` resolves to `ember` (dark preference) or `parchment` (light preference).
 */
export type Theme =
  | 'ember'
  | 'blue-slate'
  | 'parchment'
  | 'slate'
  | 'terminal-yellow'
  | 'terminal-green'
  | 'terminal-nixie'
  | 'system'
  /**
   * 2026-06-01 — user-authored custom theme. When this is the active value,
   * the companion `activeCustomThemeId` field identifies WHICH custom theme.
   * ThemeProvider reads both, looks up the theme via the useCustomThemes
   * TanStack Query cache, and applies its tokens.
   */
  | 'custom';

/**
 * 2026-05-19 — localStorage key prefix migrated from `aletheia.*` →
 * `aletheia.*` during the rebrand. Helper reads the new key first; if
 * empty, falls back to the legacy key AND copies the value to the new
 * one (one-shot migration so a returning user keeps their prefs). Empty
 * on both → returns null.
 */
function loadWithLegacyFallback(newKey: string, legacyKey: string): string | null {
  if (typeof localStorage === 'undefined') return null;
  const direct = localStorage.getItem(newKey);
  if (direct !== null) return direct;
  const legacy = localStorage.getItem(legacyKey);
  if (legacy !== null) {
    try {
      localStorage.setItem(newKey, legacy);
    } catch {
      /* quota — ignore */
    }
  }
  return legacy;
}

const THEME_KEY = 'aletheia.theme';
const THEME_KEY_LEGACY = 'aletheia.theme';
const VALID_THEMES: ReadonlySet<Theme> = new Set<Theme>([
  'ember',
  'blue-slate',
  'parchment',
  'slate',
  'terminal-yellow',
  'terminal-green',
  'terminal-nixie',
  'system',
  'custom',
]);

/** 2026-06-01 — when theme === 'custom', this localStorage key holds the id
 *  of the custom theme to apply. Cleared when switching to a built-in. */
const CUSTOM_THEME_ID_KEY = 'aletheia.customThemeId';

function loadCustomThemeId(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(CUSTOM_THEME_ID_KEY);
}

/**
 * Default theme — `ember` (2026-05-30 redesign default; warm near-black with
 * an ember-orange accent). Existing users who explicitly opted into a
 * different theme keep their selection — this default only applies on first
 * run / no persisted value.
 */
const DEFAULT_THEME: Theme = 'ember';

/**
 * Remaster U5 (2026-07-04): the hidden legacy theme blocks were deleted from
 * tokens.css, so any persisted legacy id must be migrated to its nearest
 * surviving palette (dark register → slate family; pale register → parchment;
 * the blue-heavy ocean_floor → blue-slate). `dark` was already legacy (renamed
 * `oled` in facelift-v3, 2026-05-20) — it now lands on `slate` too.
 * Unknown values still degrade to DEFAULT_THEME below.
 */
const LEGACY_THEME_MIGRATION: Readonly<Record<string, Theme>> = {
  dark: 'slate',
  oled: 'slate',
  forest: 'slate',
  slate_light: 'slate',
  ocean_floor: 'blue-slate',
  light: 'parchment',
  paper: 'parchment',
  lunar: 'parchment',
};

function loadTheme(): Theme {
  const v = loadWithLegacyFallback(THEME_KEY, THEME_KEY_LEGACY);
  if (v !== null && v in LEGACY_THEME_MIGRATION) {
    const migrated = LEGACY_THEME_MIGRATION[v];
    // Re-persist under the new id so the migration only runs once.
    try {
      localStorage.setItem(THEME_KEY, migrated);
    } catch {
      /* quota — ignore */
    }
    return migrated;
  }
  return v !== null && VALID_THEMES.has(v as Theme) ? (v as Theme) : DEFAULT_THEME;
}

/** Pinned notes are persisted to localStorage as a JSON array of NoteId strings.
 *  v4 P5 (GAP_ANALYSIS.md §X3) — the sidebar Pinned section renders these at
 *  the top of the notebook tree. Capped at a generous 50 to keep the section
 *  scannable; older pins fall off when the limit is hit. */
const PINNED_KEY = 'aletheia.pinnedNoteIds';
const PINNED_KEY_LEGACY = 'aletheia.pinnedNoteIds';
const MAX_PINNED = 50;

/** Recently-used editor font colours — the swatch picker surfaces the last 8. */
const RECENT_COLORS_KEY = 'aletheia.recentEditorColors';
const MAX_RECENT_COLORS = 8;

/** Inline chip-visibility preference — persisted to localStorage so the user's
 *  on/off choice survives reload. The toolbar toggle in EditorSurface
 *  flips it; both decorator extensions (hashtag, entity) check the flag
 *  and skip work when off. Defaults to TRUE — most users want the chips. */
const CHIPS_ENABLED_KEY = 'aletheia.chipsEnabled';
const CHIPS_ENABLED_KEY_LEGACY = 'aletheia.chipsEnabled';

function loadChipsEnabled(): boolean {
  const v = loadWithLegacyFallback(CHIPS_ENABLED_KEY, CHIPS_ENABLED_KEY_LEGACY);
  return v === null ? true : v === '1';
}

/** Editor right-hand details pane (meta-sidecar) collapsed state — persisted so the
 *  user's choice survives reload. Defaults to FALSE (pane shown). */
const META_COLLAPSED_KEY = 'aletheia.metaCollapsed';
function loadMetaCollapsed(): boolean {
  if (typeof localStorage === 'undefined') return false;
  return localStorage.getItem(META_COLLAPSED_KEY) === '1';
}

/** Left notebooks pane collapsed state (reading/presentation mode) — same pattern as the
 *  meta sidecar: persisted, defaults to FALSE (pane shown). */
const NOTEBOOKS_COLLAPSED_KEY = 'aletheia.notebooksCollapsed';
function loadNotebooksCollapsed(): boolean {
  if (typeof localStorage === 'undefined') return false;
  return localStorage.getItem(NOTEBOOKS_COLLAPSED_KEY) === '1';
}

/** Editor header (crumb + title + meta row + toolbar) collapsed state (reading/presentation
 *  mode) — persisted, defaults to FALSE (header shown). */
const EDITOR_HEAD_COLLAPSED_KEY = 'aletheia.editorHeadCollapsed';
function loadEditorHeadCollapsed(): boolean {
  if (typeof localStorage === 'undefined') return false;
  return localStorage.getItem(EDITOR_HEAD_COLLAPSED_KEY) === '1';
}

/**
 * Phase 12 of the 2026-05-19 extraction-remediation plan — editor state persistence
 * so a refresh from anywhere doesn't wipe what was open.
 *
 * Two new keys:
 *  - aletheia.lastEditorNoteId — last note opened in the editor (UUID string). When
 *    /editor is visited with no noteId, EditorSurface redirects to /editor/{this} if
 *    set. Updated by EditorSurface on every successful note load.
 *  - aletheia.expandedNotebookIds — JSON array of notebook ids the user has expanded
 *    in the NotebookTree. NotebookRow now reads/writes this instead of local useState
 *    so expansion survives reload. The auto-expand-on-active-path behavior still
 *    runs on top, so a brand-new note path expands its parents automatically.
 *
 * Both wrap localStorage in try/catch — quota-exceeded is non-fatal (the worst case
 * is "you have to re-expand the tree", which is what already happens).
 */
const LAST_EDITOR_NOTE_KEY = 'aletheia.lastEditorNoteId';
const EXPANDED_NOTEBOOKS_KEY = 'aletheia.expandedNotebookIds';
const NOTEBOOK_EXPANSION_KEY = 'aletheia.notebookExpansion';
const MAX_EXPANDED_NOTEBOOKS = 200; // generous; the tree is bounded by corpus size
const NOTEBOOK_SORT_KEY = 'aletheia.notebookSort';

function loadLastEditorNoteId(): string | null {
  if (typeof localStorage === 'undefined') return null;
  const v = localStorage.getItem(LAST_EDITOR_NOTE_KEY);
  return v && v.length > 0 ? v : null;
}

function saveLastEditorNoteId(noteId: string | null): void {
  if (typeof localStorage === 'undefined') return;
  try {
    if (noteId === null) {
      localStorage.removeItem(LAST_EDITOR_NOTE_KEY);
    } else {
      localStorage.setItem(LAST_EDITOR_NOTE_KEY, noteId);
    }
  } catch {
    /* quota — ignore */
  }
}

function loadExpandedNotebooks(): string[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(EXPANDED_NOTEBOOKS_KEY);
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((v): v is string => typeof v === 'string')
      .slice(0, MAX_EXPANDED_NOTEBOOKS);
  } catch {
    return [];
  }
}

function saveExpandedNotebooks(ids: string[]): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(
      EXPANDED_NOTEBOOKS_KEY,
      JSON.stringify(ids.slice(0, MAX_EXPANDED_NOTEBOOKS)),
    );
  } catch {
    /* quota — ignore */
  }
}

/**
 * Tristate expansion map. Replaces the boolean Set model that confused
 * "explicitly collapsed by user" with "not yet recorded" — the same null
 * state, which meant the auto-expand effect couldn't distinguish them and
 * immediately reverted manual collapses on active-path notebooks.
 *
 * - `'expanded'`  → user-confirmed open (or auto-expanded from path).
 * - `'collapsed'` → user EXPLICITLY closed it. Auto-expand will NOT override.
 * - absent        → no record; auto-expand-on-active-path can fire.
 */
export type NotebookExpansion = 'expanded' | 'collapsed';

function loadNotebookExpansion(): Record<string, NotebookExpansion> {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(NOTEBOOK_EXPANSION_KEY);
    if (raw === null) {
      // Migrate the legacy expandedNotebookIds → tristate (everything in
      // the old list becomes 'expanded'; nothing maps to 'collapsed').
      const legacy = loadExpandedNotebooks();
      const out: Record<string, NotebookExpansion> = {};
      for (const id of legacy) out[id] = 'expanded';
      return out;
    }
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};
    const out: Record<string, NotebookExpansion> = {};
    for (const [k, v] of Object.entries(parsed)) {
      if (v === 'expanded' || v === 'collapsed') out[k] = v;
    }
    return out;
  } catch {
    return {};
  }
}

function saveNotebookExpansion(map: Record<string, NotebookExpansion>): void {
  if (typeof localStorage === 'undefined') return;
  try {
    // Cap by truncating the oldest entries — rare to hit, but bounded so
    // a large corpus doesn't blow up the localStorage quota.
    const entries = Object.entries(map).slice(-MAX_EXPANDED_NOTEBOOKS);
    localStorage.setItem(NOTEBOOK_EXPANSION_KEY, JSON.stringify(Object.fromEntries(entries)));
  } catch {
    /* quota — ignore */
  }
}

/**
 * Notebook sort mode. Applied client-side to the notebook tree AND the
 * per-notebook note list. "manual" preserves the backend-returned order
 * (which is the seed for a future drag-and-drop reorder).
 */
export type NotebookSortMode =
  | 'alpha_asc'
  | 'alpha_desc'
  | 'created_desc'
  | 'created_asc'
  | 'manual';
const NOTEBOOK_SORT_DEFAULT: NotebookSortMode = 'alpha_asc';
const _NOTEBOOK_SORT_VALUES: ReadonlySet<string> = new Set<NotebookSortMode>([
  'alpha_asc',
  'alpha_desc',
  'created_desc',
  'created_asc',
  'manual',
]);

function loadNotebookSort(): NotebookSortMode {
  if (typeof localStorage === 'undefined') return NOTEBOOK_SORT_DEFAULT;
  const v = localStorage.getItem(NOTEBOOK_SORT_KEY);
  if (v !== null && _NOTEBOOK_SORT_VALUES.has(v)) return v as NotebookSortMode;
  return NOTEBOOK_SORT_DEFAULT;
}

function saveNotebookSort(mode: NotebookSortMode): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(NOTEBOOK_SORT_KEY, mode);
  } catch {
    /* quota — ignore */
  }
}

function loadPinned(): string[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = loadWithLegacyFallback(PINNED_KEY, PINNED_KEY_LEGACY);
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v): v is string => typeof v === 'string').slice(0, MAX_PINNED);
  } catch {
    return [];
  }
}

function savePinned(ids: string[]): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(PINNED_KEY, JSON.stringify(ids));
  } catch {
    /* quota exceeded — silently drop */
  }
}

/** Pinned NOTEBOOK ids — the sidebar Pinned section also surfaces pinned folders. */
const PINNED_NOTEBOOKS_KEY = 'aletheia.pinnedNotebookIds';

function loadStringArray(key: string, cap: number): string[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v): v is string => typeof v === 'string').slice(0, cap);
  } catch {
    return [];
  }
}

function saveStringArray(key: string, ids: string[]): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(key, JSON.stringify(ids));
  } catch {
    /* quota — ignore */
  }
}

/**
 * Collapsed right-panel sections (editor meta sidecar: Outline / Tags / Action items /
 * Entities / Backlinks / Details). Persisted as a `{ sectionId: true }` map of the sections
 * the user has collapsed; absent = expanded (the default). Survives reload.
 */
const COLLAPSED_PANELS_KEY = 'aletheia.collapsedPanels';

function loadCollapsedPanels(): Record<string, boolean> {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(COLLAPSED_PANELS_KEY);
    if (raw === null) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};
    const out: Record<string, boolean> = {};
    for (const [k, v] of Object.entries(parsed)) if (v === true) out[k] = true;
    return out;
  } catch {
    return {};
  }
}

function saveCollapsedPanels(map: Record<string, boolean>): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(COLLAPSED_PANELS_KEY, JSON.stringify(map));
  } catch {
    /* quota — ignore */
  }
}

/**
 * Editor magnification for the rich-text view (1.0 = 100%). Persisted to localStorage; driven by
 * Ctrl/pinch-scroll + the bottom zoom slider. Clamped to a sane reading range and rounded to whole
 * percent so the slider + label stay tidy.
 */
const EDITOR_ZOOM_KEY = 'aletheia.editorZoom';
export const EDITOR_ZOOM_MIN = 0.5;
export const EDITOR_ZOOM_MAX = 2.0;
const EDITOR_ZOOM_DEFAULT = 1.0;

function clampZoom(z: number): number {
  if (!Number.isFinite(z)) return EDITOR_ZOOM_DEFAULT;
  return Math.min(EDITOR_ZOOM_MAX, Math.max(EDITOR_ZOOM_MIN, Math.round(z * 100) / 100));
}

function loadEditorZoom(): number {
  if (typeof localStorage === 'undefined') return EDITOR_ZOOM_DEFAULT;
  const raw = localStorage.getItem(EDITOR_ZOOM_KEY);
  if (raw === null) return EDITOR_ZOOM_DEFAULT;
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) ? clampZoom(n) : EDITOR_ZOOM_DEFAULT;
}

function saveEditorZoom(z: number): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(EDITOR_ZOOM_KEY, String(z));
  } catch {
    /* quota — ignore */
  }
}

/**
 * Editor side-margin for the rich-text view, as a percent of the pane width reserved on EACH
 * side (0 = full-bleed). The content column autofits the pane minus these margins (no fixed
 * max-width). Persisted; driven by the margin slider next to the zoom slider.
 */
const EDITOR_MARGIN_KEY = 'aletheia.editorMargin';
export const EDITOR_MARGIN_MIN = 0;
export const EDITOR_MARGIN_MAX = 30;
export const EDITOR_MARGIN_DEFAULT = 8;

function clampMargin(m: number): number {
  if (!Number.isFinite(m)) return EDITOR_MARGIN_DEFAULT;
  return Math.min(EDITOR_MARGIN_MAX, Math.max(EDITOR_MARGIN_MIN, Math.round(m)));
}

function loadEditorMargin(): number {
  if (typeof localStorage === 'undefined') return EDITOR_MARGIN_DEFAULT;
  const raw = localStorage.getItem(EDITOR_MARGIN_KEY);
  if (raw === null) return EDITOR_MARGIN_DEFAULT;
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) ? clampMargin(n) : EDITOR_MARGIN_DEFAULT;
}

function saveEditorMargin(m: number): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(EDITOR_MARGIN_KEY, String(m));
  } catch {
    /* quota — ignore */
  }
}

interface UiState {
  /** The active surface — mirrors the route; set by `<AppShell>` on navigation. */
  activeSurface: SurfaceId;
  navCollapsed: boolean;
  commandPaletteOpen: boolean;
  theme: Theme;
  /** 2026-06-01 — when `theme === 'custom'`, this is the id of the active
   *  user-authored theme. Null otherwise. ThemeProvider looks this up in the
   *  `useCustomThemesQuery` cache to find the tokens to apply. */
  activeCustomThemeId: string | null;
  /** Pinned-note ids — surfaces in the sidebar Pinned section. Most-recent first. */
  pinnedNoteIds: string[];
  /** Whether inline hashtag + entity chips render in the editor body.
   *  Persisted; defaults true. Toggle in the editor's doc toolbar. */
  chipsEnabled: boolean;
  /** Whether the editor's right-hand details pane (meta-sidecar) is collapsed to the
   *  right. Persisted; defaults false (shown). */
  metaCollapsed: boolean;
  /** Whether the left notebooks pane is collapsed (reading/presentation mode).
   *  Persisted; defaults false (shown). */
  notebooksCollapsed: boolean;
  /** Whether the editor header (crumb + title + meta row + toolbar) is collapsed
   *  (reading/presentation mode). Persisted; defaults false (shown). */
  editorHeadCollapsed: boolean;
  /**
   * Phase 12 — last noteId the user had open in the editor. Persisted to
   * localStorage so a refresh from any surface can restore the editor to its
   * last state. Set by EditorSurface on every successful note load. `null`
   * on a cold start before any note has been opened.
   */
  lastEditorNoteId: string | null;
  /**
   * Phase 12 — the set of notebook ids the user has expanded in the NotebookTree.
   * Persisted. Survives refresh so the tree shape stays put. Auto-expand-on-
   * active-path still runs on top, so a fresh visit to a note expands its
   * parents even if they weren't manually expanded before.
   */
  expandedNotebookIds: string[];
  /**
   * Phase E (2026-05-20) — tristate expansion map. Persists the user's
   * explicit collapses so the auto-expand-on-active-path effect can't
   * keep reverting them. Replaces the boolean-Set model that lived in
   * expandedNotebookIds (kept for migration; deprecated read path).
   */
  notebookExpansion: Record<string, NotebookExpansion>;
  /** Phase E — sort mode applied to notebook tree + per-notebook note list. */
  notebookSort: NotebookSortMode;
  /** Pinned NOTEBOOK ids — surface in the sidebar Pinned section. Most-recent first. */
  pinnedNotebookIds: string[];
  /** Collapsed right-panel sections (editor meta sidecar). `{ sectionId: true }` = collapsed. */
  collapsedPanels: Record<string, boolean>;
  /** Recently-used editor font colours (hex strings), most-recent first, capped at 8. */
  recentEditorColors: string[];
  /** Editor magnification for the rich-text view (1.0 = 100%). Persisted; clamped to
   *  [EDITOR_ZOOM_MIN, EDITOR_ZOOM_MAX]. Driven by Ctrl/pinch-scroll + the bottom zoom slider. */
  editorZoom: number;
  /** Editor side-margin for the rich-text view — percent of pane width per side (0 = full
   *  bleed). Persisted; clamped to [EDITOR_MARGIN_MIN, EDITOR_MARGIN_MAX]. */
  editorMargin: number;
  setActiveSurface: (surface: SurfaceId) => void;
  toggleNav: () => void;
  setCommandPaletteOpen: (open: boolean) => void;
  setTheme: (theme: Theme) => void;
  /** Apply a user-authored theme. Sets `theme='custom'` + `activeCustomThemeId=themeId`. */
  setCustomTheme: (themeId: string) => void;
  /** Add a note id to the front of the pin list. Idempotent + capped at MAX_PINNED. */
  pinNote: (noteId: string) => void;
  /** Remove a note id from the pin list (no-op if absent). */
  unpinNote: (noteId: string) => void;
  /** Convenience: true when noteId is in the pinned list. */
  isPinned: (noteId: string) => boolean;
  /** Toggle / set the chip-visibility preference (persists to localStorage). */
  setChipsEnabled: (enabled: boolean) => void;
  /** Set the details-pane collapsed state (persists to localStorage). */
  setMetaCollapsed: (collapsed: boolean) => void;
  /** Set the notebooks-pane collapsed state (persists to localStorage). */
  setNotebooksCollapsed: (collapsed: boolean) => void;
  /** Set the editor-header collapsed state (persists to localStorage). */
  setEditorHeadCollapsed: (collapsed: boolean) => void;
  /** Phase 12 — record the note currently open in the editor. */
  setLastEditorNoteId: (noteId: string | null) => void;
  /** Phase 12 — expand a notebook in the tree (idempotent). */
  expandNotebook: (notebookId: string) => void;
  /** Phase 12 — collapse a notebook in the tree (no-op if not expanded). */
  collapseNotebook: (notebookId: string) => void;
  /** Phase 12 — query whether a notebook is currently expanded. */
  isNotebookExpanded: (notebookId: string) => boolean;
  /** Phase E — explicit collapse that the auto-expand effect must respect. */
  setNotebookExpansion: (notebookId: string, mode: NotebookExpansion) => void;
  /** Phase E — clear an explicit expansion record (lets auto-expand take over again). */
  clearNotebookExpansion: (notebookId: string) => void;
  /** Phase E — set the sort mode for notebooks + notes. */
  setNotebookSort: (mode: NotebookSortMode) => void;
  /** Pin a notebook (front of the list, idempotent, capped). */
  pinNotebook: (notebookId: string) => void;
  /** Unpin a notebook (no-op if absent). */
  unpinNotebook: (notebookId: string) => void;
  /** True when notebookId is pinned. */
  isNotebookPinned: (notebookId: string) => boolean;
  /** Record a font colour as recently-used (front of the list, deduped, capped at 8). */
  pushRecentEditorColor: (color: string) => void;
  /** Toggle a right-panel section's collapsed state (persists). ``defaultCollapsed`` is the
   *  section's default when the user has never toggled it, so the flip targets the effective
   *  current state. */
  togglePanelCollapsed: (sectionId: string, defaultCollapsed?: boolean) => void;
  /** True when the section is collapsed. ``defaultCollapsed`` is the value used when the user
   *  has never toggled the section (no stored entry) — lets a section default to collapsed. */
  isPanelCollapsed: (sectionId: string, defaultCollapsed?: boolean) => boolean;
  /** Set the editor zoom to an absolute level (clamped) — used by the slider. */
  setEditorZoom: (zoom: number) => void;
  /** Nudge the editor zoom by a relative delta (clamped), reading the current level — used by
   *  the Ctrl/pinch-wheel handler so there's no stale-closure drift. */
  nudgeEditorZoom: (delta: number) => void;
  /** Set the editor side-margin percent (clamped) — used by the margin slider. */
  setEditorMargin: (margin: number) => void;
}

export const useUiStore = create<UiState>((set, get) => ({
  activeSurface: 'search',
  navCollapsed: false,
  commandPaletteOpen: false,
  theme: loadTheme(),
  activeCustomThemeId: loadCustomThemeId(),
  pinnedNoteIds: loadPinned(),
  chipsEnabled: loadChipsEnabled(),
  metaCollapsed: loadMetaCollapsed(),
  notebooksCollapsed: loadNotebooksCollapsed(),
  editorHeadCollapsed: loadEditorHeadCollapsed(),
  lastEditorNoteId: loadLastEditorNoteId(),
  expandedNotebookIds: loadExpandedNotebooks(),
  notebookExpansion: loadNotebookExpansion(),
  notebookSort: loadNotebookSort(),
  pinnedNotebookIds: loadStringArray(PINNED_NOTEBOOKS_KEY, MAX_PINNED),
  collapsedPanels: loadCollapsedPanels(),
  recentEditorColors: loadStringArray(RECENT_COLORS_KEY, MAX_RECENT_COLORS),
  editorZoom: loadEditorZoom(),
  editorMargin: loadEditorMargin(),
  setActiveSurface: (activeSurface) => set({ activeSurface }),
  toggleNav: () => set((s) => ({ navCollapsed: !s.navCollapsed })),
  setCommandPaletteOpen: (commandPaletteOpen) => set({ commandPaletteOpen }),
  setChipsEnabled: (chipsEnabled) => {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(CHIPS_ENABLED_KEY, chipsEnabled ? '1' : '0');
    }
    set({ chipsEnabled });
  },
  setMetaCollapsed: (metaCollapsed) => {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(META_COLLAPSED_KEY, metaCollapsed ? '1' : '0');
    }
    set({ metaCollapsed });
  },
  setNotebooksCollapsed: (notebooksCollapsed) => {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(NOTEBOOKS_COLLAPSED_KEY, notebooksCollapsed ? '1' : '0');
    }
    set({ notebooksCollapsed });
  },
  setEditorHeadCollapsed: (editorHeadCollapsed) => {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(EDITOR_HEAD_COLLAPSED_KEY, editorHeadCollapsed ? '1' : '0');
    }
    set({ editorHeadCollapsed });
  },
  setTheme: (theme) => {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(THEME_KEY, theme);
      // Switching to a built-in clears any persisted custom-theme pointer so a
      // future return-to-custom requires an explicit re-pick.
      if (theme !== 'custom') localStorage.removeItem(CUSTOM_THEME_ID_KEY);
    }
    set({
      theme,
      activeCustomThemeId: theme === 'custom' ? get().activeCustomThemeId : null,
    });
  },
  setCustomTheme: (themeId) => {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(THEME_KEY, 'custom');
      localStorage.setItem(CUSTOM_THEME_ID_KEY, themeId);
    }
    set({ theme: 'custom', activeCustomThemeId: themeId });
  },
  pinNote: (noteId) =>
    set((s) => {
      const existing = s.pinnedNoteIds.filter((id) => id !== noteId);
      const next = [noteId, ...existing].slice(0, MAX_PINNED);
      savePinned(next);
      return { pinnedNoteIds: next };
    }),
  unpinNote: (noteId) =>
    set((s) => {
      const next = s.pinnedNoteIds.filter((id) => id !== noteId);
      savePinned(next);
      return { pinnedNoteIds: next };
    }),
  isPinned: (noteId) => get().pinnedNoteIds.includes(noteId),
  setLastEditorNoteId: (noteId) => {
    saveLastEditorNoteId(noteId);
    set({ lastEditorNoteId: noteId });
  },
  expandNotebook: (notebookId) =>
    set((s) => {
      // Mirror into the tristate map AND the legacy id list. Both writes are
      // O(1) on the map; the legacy list is kept for backward-compat
      // (anything still reading expandedNotebookIds keeps working).
      const nextExpansion = { ...s.notebookExpansion, [notebookId]: 'expanded' as const };
      saveNotebookExpansion(nextExpansion);
      if (s.expandedNotebookIds.includes(notebookId)) {
        return { notebookExpansion: nextExpansion };
      }
      const nextList = [...s.expandedNotebookIds, notebookId].slice(-MAX_EXPANDED_NOTEBOOKS);
      saveExpandedNotebooks(nextList);
      return { expandedNotebookIds: nextList, notebookExpansion: nextExpansion };
    }),
  collapseNotebook: (notebookId) =>
    set((s) => {
      // Phase E (2026-05-20) — collapse now writes 'collapsed' to the tristate
      // map (was: removed from the id list). This stops the auto-expand effect
      // in NotebookNode from re-opening manual collapses on the active-path
      // notebook. The legacy id list is also cleared to keep the two views in sync.
      const nextExpansion = { ...s.notebookExpansion, [notebookId]: 'collapsed' as const };
      saveNotebookExpansion(nextExpansion);
      const nextList = s.expandedNotebookIds.filter((id) => id !== notebookId);
      if (nextList.length !== s.expandedNotebookIds.length) {
        saveExpandedNotebooks(nextList);
      }
      return { expandedNotebookIds: nextList, notebookExpansion: nextExpansion };
    }),
  isNotebookExpanded: (notebookId) => {
    const explicit = get().notebookExpansion[notebookId];
    if (explicit === 'expanded') return true;
    if (explicit === 'collapsed') return false;
    return get().expandedNotebookIds.includes(notebookId);
  },
  setNotebookExpansion: (notebookId, mode) =>
    set((s) => {
      const nextExpansion = { ...s.notebookExpansion, [notebookId]: mode };
      saveNotebookExpansion(nextExpansion);
      return { notebookExpansion: nextExpansion };
    }),
  clearNotebookExpansion: (notebookId) =>
    set((s) => {
      if (!(notebookId in s.notebookExpansion)) return s;
      const nextExpansion = { ...s.notebookExpansion };
      delete nextExpansion[notebookId];
      saveNotebookExpansion(nextExpansion);
      return { notebookExpansion: nextExpansion };
    }),
  setNotebookSort: (mode) => {
    saveNotebookSort(mode);
    set({ notebookSort: mode });
  },
  pinNotebook: (notebookId) =>
    set((s) => {
      const next = [notebookId, ...s.pinnedNotebookIds.filter((id) => id !== notebookId)].slice(
        0,
        MAX_PINNED,
      );
      saveStringArray(PINNED_NOTEBOOKS_KEY, next);
      return { pinnedNotebookIds: next };
    }),
  unpinNotebook: (notebookId) =>
    set((s) => {
      const next = s.pinnedNotebookIds.filter((id) => id !== notebookId);
      saveStringArray(PINNED_NOTEBOOKS_KEY, next);
      return { pinnedNotebookIds: next };
    }),
  isNotebookPinned: (notebookId) => get().pinnedNotebookIds.includes(notebookId),
  pushRecentEditorColor: (color) =>
    set((s) => {
      const normalized = color.toLowerCase();
      const next = [
        normalized,
        ...s.recentEditorColors.filter((c) => c.toLowerCase() !== normalized),
      ].slice(0, MAX_RECENT_COLORS);
      saveStringArray(RECENT_COLORS_KEY, next);
      return { recentEditorColors: next };
    }),
  togglePanelCollapsed: (sectionId, defaultCollapsed = false) =>
    set((s) => {
      const next = { ...s.collapsedPanels };
      // Flip the *effective* current state (the stored value, or the section's default when the
      // user has never toggled it), storing an explicit boolean so a section whose default is
      // collapsed can be explicitly expanded (and persisted) by the user.
      const current = next[sectionId] === undefined ? defaultCollapsed : next[sectionId];
      next[sectionId] = !current;
      saveCollapsedPanels(next);
      return { collapsedPanels: next };
    }),
  isPanelCollapsed: (sectionId, defaultCollapsed = false) => {
    const v = get().collapsedPanels[sectionId];
    return v === undefined ? defaultCollapsed : v === true;
  },
  setEditorZoom: (zoom) => {
    const z = clampZoom(zoom);
    saveEditorZoom(z);
    set({ editorZoom: z });
  },
  nudgeEditorZoom: (delta) =>
    set((s) => {
      const z = clampZoom(s.editorZoom + delta);
      saveEditorZoom(z);
      return { editorZoom: z };
    }),
  setEditorMargin: (margin) => {
    const m = clampMargin(margin);
    saveEditorMargin(m);
    set({ editorMargin: m });
  },
}));
